import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { ROUTES, generatePath } from '@/constants/routes'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { BookOpen, Loader2, ArrowLeft } from "lucide-react"
import { generateCourse } from "../../api/aiTeam"

const courseTypes = [
  {
    value: "preview",
    label: "预习课",
    description: "提前了解新知识，建立直觉",
    icon: "📖",
  },
  {
    value: "new",
    label: "新授课",
    description: "系统学习新知识，掌握概念和方法",
    icon: "🎓",
  },
  {
    value: "review",
    label: "复习课",
    description: "梳理知识体系，查漏补缺",
    icon: "🔄",
  },
  {
    value: "exam_prep",
    label: "备考课",
    description: "针对考试，提升应试能力",
    icon: "🎯",
  },
  {
    value: "targeted",
    label: "专项课",
    description: "攻克特定薄弱点",
    icon: "🔧",
  },
] as const

export default function NewCoursePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [topic, setTopic] = useState(searchParams.get("topic") ?? "")
  const [courseType, setCourseType] = useState<string>(searchParams.get("courseType") ?? "new")
  const [subject, setSubject] = useState("数学")
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState("")

  const kpCode = searchParams.get("kpCode") ?? undefined
  const diagnosisId = searchParams.get("diagnosisId") ?? undefined

  // Auto-generate if coming from diagnosis page with topic + courseType
  useEffect(() => {
    if (topic && courseType === "targeted" && kpCode) {
      handleGenerate()
    }
  }, [])

  async function handleGenerate() {
    if (!topic.trim()) {
      setError("请输入学习主题")
      return
    }

    setGenerating(true)
    setError("")

    try {
      const result = await generateCourse({
        topic: topic.trim(),
        courseType: courseType as any,
        subject,
        kpCode,
        diagnosisId,
      })

      if (result.courseId) {
        navigate(generatePath(ROUTES.AI_TEAM_COURSE_DETAIL, { courseId: result.courseId }))
      } else {
        setError("课程生成失败，请重试")
      }
    } catch (err) {
      console.error("Failed to generate course:", err)
      setError("课程生成失败，请重试")
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-2xl">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate(ROUTES.AI_TEAM_COURSES)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          返回课程列表
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-600" />
            新建课程
          </CardTitle>
          <CardDescription>
            告诉 AI 你想学什么，它会为你设计个性化课程
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Topic Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              学习主题
            </label>
            <Input
              placeholder="例如：勾股定理、一次函数、概率统计..."
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={generating}
            />
            {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              学科
            </label>
            <div className="flex gap-2">
              {["数学", "物理", "化学", "英语"].map((s) => (
                <Button
                  key={s}
                  variant={subject === s ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSubject(s)}
                  disabled={generating}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>

          {/* Course Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              课程类型
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {courseTypes.map((ct) => (
                <button
                  key={ct.value}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    courseType === ct.value
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  onClick={() => setCourseType(ct.value)}
                  disabled={generating}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{ct.icon}</span>
                    <span className="font-medium">{ct.label}</span>
                  </div>
                  <p className="text-sm text-slate-500">{ct.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <Button
            className="w-full"
            size="lg"
            onClick={handleGenerate}
            disabled={generating || !topic.trim()}
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                AI 正在设计课程...
              </>
            ) : (
              "生成课程"
            )}
          </Button>

          {generating && (
            <p className="text-sm text-slate-500 text-center">
              AI 正在为你设计个性化课程，可能需要 1-2 分钟...
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
